import React from 'react';
import { Alert, Link as MuiLink } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { makeApiCall, getAuthHeaders } from '../../utils/apiConfig';
import { useAppTranslation } from '../../i18n/useAppTranslation';

export interface VendorPricingSummaryData {
  brandCount: number;
  mapRecordCount: number;
  sheetCount: number;
  sheetRecordsParsed: number;
  sheetRecordsSaved: number;
  manualMapCount: number;
  manualRebateCount: number;
}

interface Props {
  kind: 'map' | 'rebate';
  enabled: boolean;
}

/**
 * Says where the vendor's MAP / rebate data lives. These pages list only
 * records entered here; MAP from price sheets and rebate sheets sit elsewhere,
 * so the page names them with a link instead of a bare empty state.
 */
export const VendorPricingSourceSummary: React.FC<Props> = ({ kind, enabled }) => {
  const { t } = useAppTranslation();
  const { data } = useQuery<VendorPricingSummaryData | null>({
    queryKey: ['vendor-pricing-summary'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/vendor/pricing-summary', { headers });
      if (!response.ok) return null;
      const body = await response.json();
      return body?.data ?? null;
    },
    enabled,
  });

  if (!data) return null;

  if (kind === 'map') {
    if (data.mapRecordCount === 0) return null;
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        {t('vendorPricingSummary.map.body', { count: data.mapRecordCount, brands: data.brandCount, manual: data.manualMapCount })}{' '}
        <MuiLink component={RouterLink} to="/app/map-pricing-data">
          {t('vendorPricingSummary.map.link')}
        </MuiLink>
      </Alert>
    );
  }

  if (data.sheetCount === 0) return null;
  return (
    <Alert severity="info" sx={{ mb: 2 }}>
      {t('vendorPricingSummary.rebate.body', { sheets: data.sheetCount, records: data.sheetRecordsParsed, manual: data.manualRebateCount })}{' '}
      <MuiLink component={RouterLink} to="/app/dashboard">
        {t('vendorPricingSummary.rebate.link')}
      </MuiLink>
    </Alert>
  );
};
